import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, Plus, XCircle } from 'lucide-react'

import { AddTopupWristband } from './Add'
import { getTranslation } from '../../../account/services/auth'
import Loader from '../../../common/components/Loader'
import { TopupWristband } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getWristbandTopups } from '../../../core/services/topup-wristbands'
import translate from '../../../core/services/translate'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Header, PageTitle } from '@/components'
import { Button, Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

export const TopUpWristbandPage: React.FC = () => {
  const { editionId, festivalName } = useParams()

  const { size, sortBy, setSortBy, page, setPage, setSize } = useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'TOP_UP_WRISTBAND' })

  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [items, setItems] = useState<TopupWristband[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [addNew, setAddNew] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data, status } = await getWristbandTopups({
        page,
        size,
      })
      if (status === 200) {
        setItems(data.content)
        setTotalElements(data.totalElements)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size])

  const pageChange = (newPage: number) => {
    setPage(newPage)
  }

  useEffect(() => {
    getData()
  }, [getData, page, size])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const language = getTranslation()

  const columns: IColumn[] = [
    {
      title: translate(language.value, 'columns.id'),
      accessor: 'id',
    },
    {
      title: translate(language.value, 'columns.topUpOn'),
      accessor: 'topUpOn',
      render: (row) => {
        if (row.wristbandId !== null) {
          return 'Wristband SN'
        } else {
          return 'Barcode'
        }
      },
    },
    {
      title: translate(language.value, 'columns.serialNumber'),
      accessor: 'serialNumber',
      render: (row) => {
        if (row.wristbandId !== null) {
          return row.wristbandId
        } else {
          return '-'
        }
      },
    },
    {
      title: translate(language.value, 'columns.barcodes'),
      accessor: 'barcodes',
      render: (row) => {
        if (row.ticketCode !== null) {
          return row.ticketCode
        } else {
          return '-'
        }
      },
    },
    {
      title: translate(language.value, 'columns.type'),
      accessor: 'type',
      render: (row) => {
        if (row.reward) {
          return 'Reward'
        } else {
          return 'Online'
        }
      },
    },
    {
      title: translate(language.value, 'columns.amount'),
      accessor: 'amount',
    },
    {
      title: translate(language.value, 'columns.festipaySynced'),
      accessor: 'festipaySynced',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.festipaySynced ? (
            <CheckCircle className="w-4 h-4 text-accent-2" />
          ) : (
            <XCircle className="w-4 h-4 text-accent-3" />
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Top Up Wristband"
        actions={
          hasEditPermission && (
            <Button
              variant="primary-bordered"
              onClick={() => {
                setAddNew(true)
              }}
            >
              Add New
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle
          title="Top Up Wristband"
          subtitle={translate(language.value, 'topUpWristband.title')}
          className="mb-5"
        />
        <Table
          handleSortBy={handleSortBy}
          columns={columns}
          data={items}
          columnWidth="15%"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <AddTopupWristband
        visible={addNew}
        setVisible={setAddNew}
        setItems={setItems}
        page={page}
        size={size}
        setTotalElements={setTotalElements}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
